// A-08 — Layout détail dossier avec onglets.
// A-15 — Action archiver en header.
import { usePageTitle } from '../../store/usePageTitleStore';
import { dossiersByJurisdictionHref, dossiersByTypeHref } from '../../lib/dossiers/dossierListLinks';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams, NavLink, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Archive, ArchiveRestore, ChevronDown,
  Lock, Pencil, RotateCcw, MoreHorizontal, Trash2,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { Spinner } from '../ui/Spinner';
import { Button } from '../ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuthStore } from '../../store/useAuthStore';
import { asCollaboratorRole, canAccessFeature } from '../../services/auth/permissions';
import {
  useArchiveDossier, useDeleteDossier, useDossier, useDossierMembers,
  useRenameDossier, useReopenDossier, useUnarchiveDossier,
} from '../../hooks/queries/useDossiers';
import { DOSSIER_TYPE_LABELS } from '../../services/dossiers/dossierService';
import { DossierStageStepper } from './DossierStageStepper';
import { ChangeFriseDialog } from './ChangeFriseDialog';
import { DossierOverviewTab } from './DossierOverviewTab';
import { DossierPartiesTab } from './DossierPartiesTab';
import { DossierTimelineTab } from './DossierTimelineTab';
import { DossierFilesTab } from '../files/DossierFilesTab';
import { DossierDiscussionTab } from './DossierDiscussionTab';
import { DossierTasksTab } from './DossierTasksTab';
import { DossierAgendaTab } from './DossierAgendaTab';
import { differenceInCalendarDays } from 'date-fns';
import { FaArrowLeft } from '../ui/FaIcons';
import { upcomingHearingAt } from '../../lib/dossiers/upcomingHearing';
import { DOSSIER_DETAIL_TABS } from './dossierDetailTabs';
import { DossierFacturationTab } from './DossierFacturationTab';
import { useDossierQuickAccess } from '../../hooks/useDossierQuickAccess';
import { useDossierTabCounts } from '../../hooks/queries/useDossierTabCounts';
import { useDossierDiscussionUnread } from '../../hooks/queries/useMessaging';
import { formatUnreadBadge } from '../../lib/messaging/unreadMessages';
import { DossierSwitcher } from './DossierSwitcher';
import { useTabVisibilityStore } from '../../store/useTabVisibilityStore';
import { usePublishPageCrumb, usePublishPageTabSettings } from '../../store/usePageSettingsStore';

import { formatAppDate } from '../../lib/dateFormat';
export function DossierDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { pushRecent } = useDossierQuickAccess();

  // Onglet courant = premier segment d'URL après /dossiers/:id (ex. "documents").
  const currentTab = useMemo(() => {
    const m = location.pathname.match(/^\/dossiers\/[^/]+\/([^/]+)/);
    return m ? (m[1] as string) : 'resume';
  }, [location.pathname]);

  const navigate = useNavigate();
  const collaborator = useAuthStore((s) => s.collaborator);
  const isAdmin = collaborator?.role === 'admin';

  // Sélectionner l'objet `hidden` (stable tant qu'il n'est pas muté) — PAS
  // `s.hidden['dossier'] ?? []` qui créerait un nouveau tableau à chaque render
  // (boucle de re-render). On dérive le tableau après le sélecteur.
  const hiddenMap = useTabVisibilityStore((s) => s.hidden);
  const hiddenDossierTabs = hiddenMap['dossier'] ?? [];

  // Publie la config de réglage d'onglets vers l'AppHeader (icône dédiée).
  usePublishPageTabSettings(
    'dossier',
    DOSSIER_DETAIL_TABS.map((t) => ({ key: t.key, label: t.label, locked: t.key === 'resume' })),
  );

  const { data: dossier, isLoading, error } = useDossier(id);
  usePageTitle(dossier?.title);
  usePublishPageCrumb(dossier?.title);
  const { data: members } = useDossierMembers(id);
  const { data: counts } = useDossierTabCounts(id);
  const discussionUnreadLabel = formatUnreadBadge(
    useDossierDiscussionUnread(id, { viewing: currentTab === 'discussions' }),
  );
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [friseOpen, setFriseOpen] = useState(false);

  const archive = useArchiveDossier();
  const unarchive = useUnarchiveDossier();
  const reopen = useReopenDossier();
  const rename = useRenameDossier();
  const remove = useDeleteDossier();

  useEffect(() => {
    if (dossier?.id) pushRecent(dossier.id);
  }, [dossier?.id, pushRecent]);

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Spinner /></div>;
  }
  if (error || !dossier) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <p className="text-sm text-red-600">
          {error instanceof Error ? error.message : 'Dossier introuvable.'}
        </p>
        <Button variant="ghost" onClick={() => navigate('/dossiers')}>
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour à la liste
        </Button>
      </div>
    );
  }

  const isClosed = !!dossier.closed_at;
  const isArchived = !!dossier.archived_at;
  const canEdit = canAccessFeature(asCollaboratorRole(collaborator?.role), 'dossiers_edit') && !isClosed && !isArchived;
  const isLeadCounsel = !!members?.some(
    (m) => m.collaborator_id === collaborator?.id && m.role === 'lead_counsel',
  );
  const canManageMembers = (isAdmin || isLeadCounsel) && !isClosed && !isArchived;

  /** Pastille d'onglet : compteur neutre, ou alerte (retard, messages non lus). */
  function tabBadge(key: string): { text: string; hot?: boolean } | null {
    if (key === 'taches') {
      if (counts?.tachesRetard) return { text: `${counts.tachesRetard} en retard`, hot: true };
      return counts?.taches ? { text: String(counts.taches) } : null;
    }
    if (key === 'agenda') {
      const next = upcomingHearingAt(dossier!.next_hearing_at, new Date());
      if (!next) return null;
      const days = differenceInCalendarDays(new Date(next), new Date());
      return { text: days === 0 ? "Aujourd'hui" : `J-${days}` };
    }
    if (key === 'discussions') {
      return discussionUnreadLabel ? { text: discussionUnreadLabel, hot: true } : null;
    }
    const countKey = DOSSIER_DETAIL_TABS.find((tab) => tab.key === key)?.countKey;
    const count = countKey ? counts?.[countKey] : undefined;
    return count ? { text: String(count) } : null;
  }

  return (
    <div className="main-container-gdm">
      {/* Carte de titre : identité, étape compacte, actions, puis les onglets.
          Collante dans le scroller de page (PageTransition). */}
      <section className="sticky top-0 z-10 rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center gap-4 py-3 pl-3.5 pr-[18px]">
          <Link
            to="/dossiers"
            title="Retour aux dossiers"
            aria-label="Retour aux dossiers"
            className="grid h-[34px] w-[34px] flex-shrink-0 place-items-center rounded-[9px] border border-neutral-200 text-cime-text-muted transition-colors hover:border-cime-primary/30 hover:bg-[#e8f0ec] hover:text-cime-primary"
          >
            <FaArrowLeft className="h-3.5 w-3.5" aria-hidden />
          </Link>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="flex flex-wrap items-center gap-2 text-xs text-cime-text-muted">
                <span>{dossier.number}</span>
                {dossier.juridiction ? (
                  <>
                    <Dot />
                    <Link
                      to={dossiersByJurisdictionHref(dossier.juridiction)}
                      title="Voir tous les dossiers de cette juridiction"
                      className="transition-colors hover:text-cime-primary hover:underline underline-offset-2"
                    >
                      {dossier.juridiction}
                    </Link>
                    {dossier.chambre ? <span>— {dossier.chambre}</span> : null}
                  </>
                ) : null}
                {dossier.rg_number ? (
                  <>
                    <Dot />
                    <span>RG {dossier.rg_number}</span>
                  </>
                ) : null}
              </span>
              <Link
                to={dossiersByTypeHref(dossier.type_affaire)}
                title="Voir tous les dossiers de ce type"
                className="text-[10px] font-bold uppercase tracking-wide text-cime-accent border border-neutral-300 rounded px-1.5 py-0.5 transition-colors hover:border-cime-primary hover:text-cime-primary"
              >
                {DOSSIER_TYPE_LABELS[dossier.type_affaire]}
              </Link>
              {isClosed && (
                <span className="inline-flex items-center gap-1 text-xs text-neutral-600 bg-neutral-100 px-2 py-0.5 rounded">
                  <Lock className="w-3 h-3" /> Clos le {formatAppDate(dossier.closed_at)}
                </span>
              )}
              {isArchived && (
                <span className="inline-flex items-center gap-1 text-xs text-neutral-600 bg-neutral-200 px-2 py-0.5 rounded">
                  <Archive className="w-3 h-3" /> Archivé
                </span>
              )}
            </div>
            <div className="mt-[3px]">
              <DossierSwitcher currentDossier={dossier!} currentTab={currentTab} />
            </div>
          </div>

          <DossierStageStepper dossier={dossier} canTransition={canEdit} />

          {/* Actions : réservées à l'admin et au responsable (un simple membre aurait un menu vide) */}
          {(isAdmin || isLeadCounsel) && (
          <div className="flex flex-shrink-0 items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary">
                  <MoreHorizontal className="w-4 h-4 mr-1.5" /> Actions
                  <ChevronDown className="w-3.5 h-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="z-[100] min-w-56 bg-white border-neutral-200 rounded-cime"
              >
                {(isAdmin || isLeadCounsel) && !isClosed && !isArchived && (
                  <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
                    <Pencil className="w-4 h-4 text-neutral-600" /> Renommer le dossier
                  </DropdownMenuItem>
                )}
                {(isAdmin || isLeadCounsel) && !isArchived && (
                  <DropdownMenuItem onSelect={() => setFriseOpen(true)}>
                    <Pencil className="w-4 h-4 text-neutral-600" /> Changer la frise
                  </DropdownMenuItem>
                )}
                {isClosed && isAdmin && (
                  <DropdownMenuItem onSelect={() => reopen.mutate(dossier.id)}>
                    <RotateCcw className="w-4 h-4 text-blue-600" /> Réouvrir
                  </DropdownMenuItem>
                )}
                {(isAdmin || isLeadCounsel) && !isArchived && (
                  <DropdownMenuItem
                    onSelect={() => {
                      if (window.confirm('Archiver ce dossier ? Il sera masqué de la liste par défaut.')) {
                        archive.mutate(dossier.id);
                      }
                    }}
                  >
                    <Archive className="w-4 h-4 text-neutral-600" /> Archiver
                  </DropdownMenuItem>
                )}
                {isArchived && isAdmin && (
                  <DropdownMenuItem onSelect={() => unarchive.mutate(dossier.id)}>
                    <ArchiveRestore className="w-4 h-4 text-neutral-600" /> Désarchiver
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => setDeleteOpen(true)}
                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" /> Supprimer le dossier
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          )}
        </div>

        <nav aria-label="Onglets du dossier" className="flex items-center overflow-x-auto border-t border-[#e6e2d8] px-2.5">
          {DOSSIER_DETAIL_TABS.filter((tab) => !hiddenDossierTabs.includes(tab.key)).map((tab) => {
            const badge = tabBadge(tab.key);
            const TabIcon = tab.icon;
            return (
              <NavLink
                key={tab.key}
                to={`/dossiers/${dossier.id}/${tab.to}`}
                end
                className={({ isActive }) =>
                  cn(
                    'group -mb-px inline-flex items-center whitespace-nowrap border-b-2 px-3 pb-2.5 pt-[11px] text-sm transition-colors',
                    isActive
                      ? 'border-cime-primary font-semibold text-cime-primary'
                      : 'border-transparent font-medium text-cime-text-muted hover:text-cime-text-primary',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <TabIcon
                      className={cn('mr-[7px] h-[13px] w-[13px]', isActive ? 'opacity-100' : 'opacity-70')}
                      aria-hidden
                    />
                    {tab.label}
                    {badge ? (
                      <span
                        className={cn(
                          'ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums leading-none',
                          badge.hot
                            ? 'bg-[#fdece6] text-[#c8431f]'
                            : isActive
                              ? 'bg-[#e8f0ec] text-cime-primary'
                              : 'bg-neutral-100 text-neutral-400',
                        )}
                      >
                        {badge.text}
                      </span>
                    ) : null}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
      </section>

      {friseOpen && (
        <ChangeFriseDialog
          dossierId={dossier.id}
          open={friseOpen}
          onClose={() => setFriseOpen(false)}
        />
      )}

      {/* Contenu onglet + rail de synthèse */}
      <div className="flex gap-5 items-start py-5 pb-10">
          <div
            className={cn(
              'flex-1 min-w-0 flex flex-col',
              !BARE_TABS.includes(currentTab) && 'bg-cime-surface rounded-cime border border-neutral-200 shadow-sm',
            )}
          >
            <div className={cn('flex-1 min-h-0 flex flex-col', !BARE_TABS.includes(currentTab) && 'px-5 py-4')}>
            <Routes>
              <Route index element={<Navigate to="resume" replace />} />
              <Route
                path="resume"
                element={<DossierOverviewTab dossier={dossier} canEdit={canEdit} canManageMembers={canManageMembers} />}
              />
              <Route
                path="equipe"
                element={<Navigate to={`/dossiers/${dossier.id}/resume`} replace />}
              />
              <Route
                path="parties"
                element={<DossierPartiesTab dossier={dossier} canEdit={canEdit} />}
              />
              <Route
                path="timeline"
                element={<DossierTimelineTab dossier={dossier} />}
              />
              <Route
                path="prestations"
                element={<Navigate to={`/dossiers/${dossier.id}/facturation`} replace />}
              />
              <Route
                path="documents/*"
                element={<DossierFilesTab dossierId={dossier.id} />}
              />
              <Route
                path="emails"
                element={<Navigate to={`/dossiers/${dossier.id}/documents?email=1`} replace />}
              />
              <Route
                path="discussions"
                element={<DossierDiscussionTab dossierId={dossier.id} />}
              />
              <Route
                path="taches"
                element={<DossierTasksTab dossierId={dossier.id} />}
              />
              <Route
                path="agenda"
                element={<DossierAgendaTab dossier={dossier} />}
              />
              <Route
                path="calendrier"
                element={<Navigate to={`/dossiers/${dossier.id}/agenda`} replace />}
              />
              <Route
                path="facturation"
                element={<DossierFacturationTab dossierId={dossier.id} readOnly={isClosed || isArchived} />}
              />
            </Routes>
            </div>
          </div>
      </div>

      {renameOpen && (
        <RenameDossierModal
          currentTitle={dossier.title}
          isPending={rename.isPending}
          onSubmit={(title) =>
            rename.mutate(
              { id: dossier.id, title },
              { onSuccess: () => setRenameOpen(false) },
            )
          }
          onClose={() => setRenameOpen(false)}
        />
      )}

      {deleteOpen && (
        <DeleteDossierModal
          number={dossier.number}
          title={dossier.title}
          isPending={remove.isPending}
          onConfirm={() =>
            remove.mutate(dossier.id, {
              onSuccess: () => {
                setDeleteOpen(false);
                navigate('/dossiers');
              },
            })
          }
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </div>
  );
}

function DeleteDossierModal({
  number,
  title,
  isPending,
  onConfirm,
  onClose,
}: {
  number: string;
  title: string;
  isPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-cime shadow-xl max-w-md w-full">
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center gap-2">
          <Trash2 className="w-5 h-5 text-red-600" />
          <h2 className="text-base font-semibold text-cime-text-primary">
            Supprimer le dossier
          </h2>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-cime-text-primary">
            Voulez-vous vraiment supprimer le dossier{' '}
            <span className="text-cime-text-muted">{number}</span>{' '}
            <span className="font-medium">« {title} »</span> ?
          </p>
          <p className="text-sm text-cime-text-muted">
            Le dossier sera retiré de la liste. Cette action peut être annulée par un
            administrateur si nécessaire.
          </p>
        </div>
        <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose} disabled={isPending}>
            Annuler
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isPending ? 'Suppression…' : 'Supprimer'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function RenameDossierModal({
  currentTitle,
  isPending,
  onSubmit,
  onClose,
}: {
  currentTitle: string;
  isPending: boolean;
  onSubmit: (title: string) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(currentTitle);
  const trimmed = title.trim();
  const canSubmit = trimmed.length > 0 && trimmed !== currentTitle && !isPending;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-cime shadow-xl max-w-md w-full">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) onSubmit(trimmed);
          }}
        >
          <div className="px-5 py-4 border-b border-neutral-200">
            <h2 className="text-base font-semibold text-cime-text-primary">
              Renommer le dossier
            </h2>
          </div>
          <div className="px-5 py-4">
            <label className="block text-sm font-medium text-cime-text-primary mb-1.5">
              Titre
            </label>
            <input
              autoFocus
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-cime focus:outline-none focus:ring-2 focus:ring-cime-primary"
            />
          </div>
          <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? 'Enregistrement…' : 'Renommer'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

/** Onglets qui posent leurs propres cartes : pas de cadre blanc autour. */
const BARE_TABS = ['resume', 'parties', 'documents', 'taches', 'agenda', 'discussions', 'facturation'];

function Dot() {
  return <span aria-hidden className="h-[3px] w-[3px] rounded-full bg-neutral-300" />;
}
